import { type ComponentType } from "react";
import {
  Calculator,
  ChartSpline,
  Gauge,
  House,
  IdCard,
  Inbox,
  LifeBuoy,
  MapPin,
  MessageCircle,
  MessageCircleQuestion,
  Search,
  Upload,
  Users,
  Zap,
} from "lucide-react";

import type { DashboardPrimaryView, DashboardView } from "../lib/dashboard";
import { marketplaceBrowseHref } from "../lib/marketplace-route";

// ── Nav icon + item types ─────────────────────────────────────────────────────

export type DashboardNavIcon = ComponentType<{ className?: string }>;

export type DashboardNavItem =
  | {
      icon: DashboardNavIcon;
      label: string;
      status: "live";
      trailingIcon?: DashboardNavIcon;
      view: DashboardPrimaryView;
    }
  | {
      /** Optional live count badge (Decision 232: sourced from existing offersInbox read). */
      badge?: number;
      icon: DashboardNavIcon;
      label: string;
      status: "live";
      href: string;
      trailingIcon?: DashboardNavIcon;
    }
  | {
      icon: DashboardNavIcon;
      label: string;
      status: "live";
      action: "feedback";
      trailingIcon?: DashboardNavIcon;
    }
  | {
      /** Hover copy displayed as `title` attribute on the disabled button. */
      hoverCopy?: string;
      icon: DashboardNavIcon;
      label: string;
      status: "planned";
      trailingIcon?: DashboardNavIcon;
    };

export type DashboardNavGroup = {
  label: string;
  items: DashboardNavItem[];
};

// Plan 435 M1 (mfh-nav-regroup, task:mfh-nav-regroup). Marketplace is the
// product; readiness tools are the means (decision 2026-10-06). MARKETPLACE
// leads with Home (mounts HomeView — plan 435 M1 — at the default `/dashboard`
// view, `view: "overview"` unchanged); PREPARE holds every readiness tool
// (Properties joins it here) and is always expanded (no collapse control).
// Ask Sill is NOT a sidebar item: since plan 435 M6 it is a drawer opened
// from the header "Ask Sill" pill (desktop) and the bottom tab bar
// (mobile); a heading-less standalone sidebar group read as stray. Passport
// sits second in MARKETPLACE (the buyer's listing), alongside the hero's
// readiness chip and the `?view=passport` deep link/aliases.
const SIDEBAR_NAV_GROUPS: DashboardNavGroup[] = [
  {
    label: "Marketplace",
    items: [
      { icon: House, label: "Home", status: "live", view: "overview" },
      // Restored 2026-10-08 (operator): the Passport must be reachable from
      // the nav, not only via the hero's readiness chip — it is the buyer's
      // listing on this marketplace.
      { icon: IdCard, label: "Passport", status: "live", view: "passport" },
      // task:browse-agents-disabled — M7 flips this to live + route.
      {
        icon: Search,
        label: "Browse agents",
        status: "planned",
        hoverCopy: "Browse verified agents — coming soon",
      },
      // badge injected at runtime from offersInbox count by filterDashboardNavGroups
      {
        icon: Inbox,
        label: "Your Offers",
        status: "live",
        href: "/offers",
      },
      // Messaging-inbox IA (operator placement 2026-07-20): Messages sits in
      // the MARKETPLACE group beside Your Team — conversations are with your
      // connected pros, not a Home destination. The header speech-bubble
      // icon (with the same unread badge) remains the primary entry point.
      // Gated on messaging_enabled + badge injected at runtime by
      // filterDashboardNavGroups (mirrors the Your Offers badge).
      {
        icon: MessageCircle,
        label: "Messages",
        status: "live",
        href: "/threads",
      },
      // Plan 291 live-QA follow-up (item B): repointed from the Settings
      // deep-link to the first-class /team index page. Settings' "Your
      // Team" tab was retired entirely (operator feedback fix) — /team is
      // now the sole place to view/manage connected pros.
      {
        icon: Users,
        label: "Your Team",
        status: "live",
        href: "/team",
      },
    ],
  },
  {
    label: "Prepare",
    items: [
      // 2026-10-08: the one-page Readiness view (score, path to ready,
      // next moves, factors) — the old Overview score card's tabs merged.
      { icon: Gauge, label: "Readiness", status: "live", view: "readiness" },
      { icon: MapPin, label: "Properties", status: "live", view: "properties" },
      {
        icon: Calculator,
        label: "Calculators",
        status: "live",
        href: "/calculators",
      },
      {
        icon: ChartSpline,
        label: "Rate Watch",
        status: "live",
        view: "rate-watch",
      },
      { icon: Upload, label: "Documents", status: "live", view: "documents" },
      {
        icon: Zap,
        label: "Automation",
        status: "live",
        href: "/dashboard/automation",
      },
    ],
  },
  {
    label: "Support",
    items: [
      { icon: LifeBuoy, label: "Help Center", status: "live", href: "/help" },
      {
        icon: MessageCircleQuestion,
        label: "Send feedback",
        status: "live",
        action: "feedback",
      },
    ],
  },
];

export function filterDashboardNavGroups({
  marketplaceEnabled = false,
  hasConnectedAgent = false,
  feedEnabled,
  messagingEnabled = false,
  messagesUnreadCount,
  offersCount,
}: {
  marketplaceEnabled?: boolean;
  /** Pre-launch invited-buyer posture: true when the buyer already has a
   *  connected agent (`DashboardSnapshot.agentConnected`). While the
   *  SILL-504 launch gate is closed, the "Browse agents · Soon" planned
   *  stub is suppressed for these buyers — teasing "browse other agents"
   *  at a client their own agent invited onto Sill undercuts the referral
   *  motion. Once `marketplaceEnabled` is true this input is ignored and
   *  Browse renders live for everyone (recovery windows + the D9
   *  own-client exclusion depend on connected buyers being able to
   *  browse), so the check self-retires at launch. */
  hasConnectedAgent?: boolean;
  feedEnabled: boolean;
  /** Gates the Messages sidebar item (messaging_enabled, D19). */
  messagingEnabled?: boolean;
  /** Unread-conversation count for the Messages badge. Badge shows only
   *  when > 0 (an unread badge at "0" is noise, unlike the offers count). */
  messagesUnreadCount?: number | null;
  /** Live count for the Your Offers badge. null = not yet fetched (badge hidden). */
  offersCount?: number | null;
}): DashboardNavGroup[] {
  return SIDEBAR_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => feedEnabled || !isFeedNavItem(item))
      .filter((item) => messagingEnabled || !isMessagesNavItem(item))
      // SILL-504: Your Offers links to a gated route — drop it while the
      // single marketplace launch gate is closed (a dead link is worse
      // than an absent one). Passport + Your Team are core surfaces and
      // stay; Browse agents keeps its "planned" stub below.
      .filter((item) => marketplaceEnabled || !isOffersNavItem(item))
      // Pre-launch only: hide the planned "Browse agents" tease from buyers
      // who already have a connected agent (see hasConnectedAgent doc above).
      .filter(
        (item) =>
          marketplaceEnabled ||
          !hasConnectedAgent ||
          !isBrowseAgentsStubItem(item),
      )
      .map((item): DashboardNavItem => {
        if (item.label === "Browse agents" && marketplaceEnabled) {
          return {
            icon: item.icon,
            label: "Browse agents",
            status: "live",
            href: marketplaceBrowseHref("agent"),
          };
        }
        // Inject the live badge count onto the Your Offers href item.
        if (isOffersNavItem(item) && offersCount != null) {
          return { ...item, badge: offersCount };
        }
        // Inject the unread badge onto the Messages item (WS6) — only when
        // there ARE unread conversations. Inline narrowing (status/href)
        // mirrors the offers branch so TS resolves the badge-capable variant.
        if (
          item.status === "live" &&
          "href" in item &&
          item.href === "/threads" &&
          messagesUnreadCount != null &&
          messagesUnreadCount > 0
        ) {
          return { ...item, badge: messagesUnreadCount };
        }
        return item;
      }),
  })).filter((group) => group.items.length > 0);
}

function isBrowseAgentsStubItem(item: DashboardNavItem): boolean {
  return item.status === "planned" && item.label === "Browse agents";
}

function isFeedNavItem(item: DashboardNavItem): boolean {
  return item.status === "live" && "view" in item && item.view === "feed";
}

function isMessagesNavItem(item: DashboardNavItem): boolean {
  return item.status === "live" && "href" in item && item.href === "/threads";
}

function isOffersNavItem(
  item: DashboardNavItem,
): item is Extract<DashboardNavItem, { status: "live"; href: string }> {
  return item.status === "live" && "href" in item && item.href === "/offers";
}

// ── ShellActiveView + computeSidebarWidth ─────────────────────────────────────

export type ShellActiveView =
  | DashboardView
  | "automation"
  | "calculators"
  | "help"
  | "offers"
  | "marketplace-browse"
  | "messages"
  | "publish"
  | "settings"
  | "team";

/**
 * Single-source sidebar-width computation shared by DashboardPage and
 * DashboardShell. Returns the CSS value for `--dashboard-sidebar-width`.
 * `usesAbbreviatedSidebar` lets routed workspaces reuse the same 76/264 px
 * rail calculation without duplicating sidebar width values.
 */
export function computeSidebarWidth(
  activeView: ShellActiveView | undefined,
  isAdvisorRailExpanded = false,
  usesAbbreviatedSidebar = activeView === "advisor",
): string {
  if (usesAbbreviatedSidebar) {
    return isAdvisorRailExpanded ? "264px" : "76px";
  }
  return "264px";
}

// ── Sidebar passport card (plan 435 M1, mfh-sidebar-passport-card) ────────────

/**
 * Live state for the sidebar's passport card. Named to match the roadmap's
 * "live / queued / not-live / request-pending / connected" exactly.
 */
export type SidebarPassportLiveState =
  | "connected"
  | "request-pending"
  | "queued"
  | "live"
  | "not-live";

export interface SidebarPassportLiveStatusInput {
  /** `snapshot.portalDiscoverable` — NEVER `publishedAt`, which stays set
   *  after unpublish (tech-spec Concepts "Live"). */
  portalDiscoverable: boolean;
  /** The buyer's area is queued (not yet a launched metro) while published —
   *  from the already-fetched `useWaitlist()` read (non-null = queued). No
   *  new marketplace read: this card must not issue one for the live/queued
   *  distinction. */
  isQueued: boolean;
  /** `marketplaceConnection?.status`, already fetched by DashboardPage. */
  connectionStatus?: string | null;
  /** Whether the buyer has a pending standing-offer request, from the SAME
   *  `useMarketplaceConnection` hook call (folded `pendingRequest`, no new
   *  read). */
  hasPendingRequest: boolean;
}

/**
 * Pure derivation — no network, no new reads. Precedence: an active
 * connection outranks everything (the buyer is already connected even if a
 * stale pending request or publish state lingers); a pending request
 * outranks publish state; published-and-queued outranks published-and-live;
 * unpublished is the fallback.
 */
export function deriveSidebarPassportLiveState(
  input: SidebarPassportLiveStatusInput,
): SidebarPassportLiveState {
  if (input.connectionStatus === "active") return "connected";
  if (input.hasPendingRequest) return "request-pending";
  if (!input.portalDiscoverable) return "not-live";
  return input.isQueued ? "queued" : "live";
}

export interface SidebarPassportLiveStateCopy {
  label: string;
  dotClassName: string;
}

export const SIDEBAR_PASSPORT_LIVE_STATE_COPY: Record<
  SidebarPassportLiveState,
  SidebarPassportLiveStateCopy
> = {
  connected: { label: "Connected", dotClassName: "bg-success" },
  "request-pending": { label: "Request pending", dotClassName: "bg-warning" },
  queued: { label: "Waitlisted", dotClassName: "bg-warning" },
  // Matches the existing PassportPublishBlock vocabulary ("Live to agents").
  live: { label: "Live to agents", dotClassName: "bg-success" },
  "not-live": { label: "Not live", dotClassName: "bg-muted-foreground" },
};

/**
 * "N agents competing" line copy. `null`/`undefined`/`<= 0` → no line (no
 * competing-agent count source exists yet — M3's `AgentPreview` browse read
 * is where this will eventually come from; omitting it here is honest, not
 * a bug).
 */
export function sidebarPassportCompetingCopy(
  competingCount: number | null | undefined,
): string | null {
  if (!competingCount || competingCount <= 0) return null;
  return `${competingCount.toString()} agent${competingCount === 1 ? "" : "s"} competing`;
}
